import React, { useEffect, useState } from "react";
import { History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const ACTION_LABEL = { created: "Added", updated: "Changed", deleted: "Deleted", released: "Released to feed" };
const VIA_LABEL = { workbook: "Workbook upload", portal: "Portal" };

const value = (field, raw) => {
  if (raw === null || raw === undefined || raw === "") return "—";
  if (field === "rate_percent") return `${(Number(raw) * 100).toFixed(2)}%`;
  return String(raw);
};

const when = (iso) =>
  new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

/** Who changed a rate, when, and what the previous value was. */
export default function RateChangeLog({ company, rateType = "", startDate = "", limit = 200, emptyNote }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!company) return;
    setLoading(true);
    api.get("/admin/rates/changes", {
      params: { company, rate_type: rateType, start_date: startDate, limit },
    })
      .then(({ data }) => setRows(data.rows))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [company, rateType, startDate, limit]);

  if (loading) return <p className="text-sm py-6" style={{ color: "var(--muted)" }}>Loading change log…</p>;

  if (!rows.length) {
    return (
      <p className="text-sm py-6" style={{ color: "var(--muted)" }} data-testid="rate-changes-empty">
        {emptyNote || "No changes recorded yet."}
      </p>
    );
  }

  return (
    <ol className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="rate-changes">
      {rows.map((row) => (
        <li key={row.id} className="py-3.5 flex gap-3" data-testid={`rate-change-${row.id}`}>
          <History size={14} className="mt-1 shrink-0" style={{ color: "var(--muted)" }}/>
          <div className="min-w-0">
            <p className="text-sm" style={{ color: "var(--ink)" }}>
              <span className="font-bold">{ACTION_LABEL[row.action] || row.action}</span>{" "}
              {row.rate_type} · {row.company}
              {row.start_date ? <span style={{ color: "var(--muted)" }}> (from {row.start_date})</span> : null}
            </p>
            {!!row.changes.length && (
              <ul className="mt-1 space-y-0.5">
                {row.changes.map((change) => (
                  <li key={change.field} className="text-xs" style={{ color: "var(--muted)" }}>
                    {change.label}: <span style={{ color: "var(--ink)" }}>{value(change.field, change.from)}</span>
                    {" → "}
                    <span className="font-heading font-bold" style={{ color: "var(--ink)" }}>{value(change.field, change.to)}</span>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>
              {row.actor || "Unknown"} · {VIA_LABEL[row.via] || row.via} · {when(row.at)}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}
